import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { clmAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import Card from './ui/Card';
import Badge from './ui/Badge';
import SearchableSelect from './ui/SearchableSelect';
import ConfirmModal from './ui/ConfirmModal';
import Modal from './ui/Modal';
import Button from './ui/Button';

// ClmManagement — administrarea Conducătorilor Loc de Muncă pentru o firmă.
// Firma SSM stabilește cine e CLM (permanent sau temporar — înlocuitor pe concediu/maternitate),
// pe departament sau pe toată firma. Vizual: secțiuni pe departament (+ „Toată firma"), fiecare
// cu lista de mandate și un buton de adăugare. Departamentele noi apar automat (gol → „adaugă CLM").

const statusColor = { ACTIVE: 'green', SCHEDULED: 'blue', ENDED: 'gray' };

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('ro-RO') : null);

// Inițialele persoanei pentru avatarul din rând (max 2 litere).
const initials = (user) => {
  if (!user) return '—';
  const a = (user.lastName || '').trim()[0] || '';
  const b = (user.firstName || '').trim()[0] || '';
  return (a + b).toUpperCase() || '—';
};

// Paletă per tip de panou — folosită la accentul rândurilor + avatar, ca cele două
// liste (CLM vs. Împuterniciri admin) să fie distincte dintr-o privire.
const ACCENT = {
  clm: {
    avatar: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    border: 'border-l-indigo-400 dark:border-l-indigo-500',
  },
  admin: {
    avatar: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    border: 'border-l-amber-400 dark:border-l-amber-500',
  },
};

const IconUsers = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
    />
  </svg>
);

const IconShield = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
    />
  </svg>
);

const ClmManagement = ({ companyId }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // CLM: { departmentId | null } când e deschis
  const [adminModalOpen, setAdminModalOpen] = useState(false); // împuternicire admin
  const [editing, setEditing] = useState(null); // { mandate, isAdmin } — modal de editare
  const [confirm, setConfirm] = useState(null); // { title, message, confirmLabel, variant, action }
  const [confirmLoading, setConfirmLoading] = useState(false);
  // „Încheie acum" cu motiv opțional: { mandateId, isAdmin }
  const [endTarget, setEndTarget] = useState(null);
  const [endReason, setEndReason] = useState('');
  const [endLoading, setEndLoading] = useState(false);

  // Rulează acțiunea confirmată în modalul drăguț; închide la succes, lasă deschis la eroare.
  const runConfirm = async () => {
    if (!confirm?.action) {
      setConfirm(null);
      return;
    }
    setConfirmLoading(true);
    try {
      await confirm.action();
      setConfirm(null);
    } catch (err) {
      showToast(err.response?.data?.error || t('clm.error'), 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clmAPI.getCompanyClm(companyId);
      setData(res.data);
    } catch (err) {
      showToast(err.response?.data?.error || t('clm.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = (mandateId) =>
    setConfirm({
      title: t('clm.confirmTitle'),
      message: t('clm.confirmDelete'),
      confirmLabel: t('common.delete'),
      variant: 'danger',
      action: async () => {
        await clmAPI.remove(companyId, mandateId);
        showToast(t('clm.deleted'), 'success');
        load();
      },
    });

  // „Încheie acum" — deschide modalul care cere un motiv opțional, apoi setează endDate = azi.
  const handleEndNow = (mandateId) => {
    setEndReason('');
    setEndTarget({ mandateId, isAdmin: false });
  };

  // Aplică încheierea (CLM sau admin) cu motivul opțional introdus.
  const runEndNow = async () => {
    if (!endTarget) return;
    setEndLoading(true);
    try {
      const payload = { endDate: new Date().toISOString() };
      if (endReason.trim()) payload.reason = endReason.trim();
      if (endTarget.isAdmin) await clmAPI.updateAdmin(companyId, endTarget.mandateId, payload);
      else await clmAPI.update(companyId, endTarget.mandateId, payload);
      showToast(t('clm.updated'), 'success');
      setEndTarget(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || t('clm.error'), 'error');
    } finally {
      setEndLoading(false);
    }
  };

  // Handlere pentru împuternicirile de admin.
  const handleDeleteAdmin = (mandateId) =>
    setConfirm({
      title: t('clm.confirmTitle'),
      message: t('clm.admin.confirmDelete'),
      confirmLabel: t('common.delete'),
      variant: 'danger',
      action: async () => {
        await clmAPI.removeAdmin(companyId, mandateId);
        showToast(t('clm.admin.deleted'), 'success');
        load();
      },
    });
  const handleEndNowAdmin = (mandateId) => {
    setEndReason('');
    setEndTarget({ mandateId, isAdmin: true });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }
  if (!data) return null;

  const renderMandate = (m, onEndNow, onDelete, onEdit, accent = 'clm') => {
    const a = ACCENT[accent] || ACCENT.clm;
    return (
      <div
        key={m.id}
        className={`flex flex-wrap items-center justify-between gap-3 py-2.5 pl-3 pr-3 rounded-lg bg-white dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50 border-l-[3px] ${a.border} hover:shadow-sm transition-shadow`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${a.avatar}`}
          >
            {initials(m.user)}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                {m.user ? `${m.user.lastName} ${m.user.firstName}` : '—'}
              </span>
              <Badge color={statusColor[m.status] || 'gray'}>{t(`clm.status.${m.status}`)}</Badge>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              <span>
                {m.endDate
                  ? `${fmtDate(m.startDate)} – ${fmtDate(m.endDate)}`
                  : `${t('clm.from')} ${fmtDate(m.startDate)} · ${t('clm.indefinite')}`}
              </span>
              {m.reason && (
                <span className="italic text-slate-400 dark:text-slate-500 truncate">
                  · „{m.reason}"
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onEdit(m)}
            className="text-xs font-medium px-2 py-1 rounded-md border border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800/60 dark:text-blue-400 dark:hover:bg-blue-900/30"
          >
            {t('common.edit')}
          </button>
          {m.status !== 'ENDED' && (
            <button
              onClick={() => onEndNow(m.id)}
              className="text-xs font-medium px-2 py-1 rounded-md border border-orange-200 text-orange-600 hover:bg-orange-50 dark:border-orange-800/60 dark:text-orange-400 dark:hover:bg-orange-900/30"
            >
              {t('clm.endNow')}
            </button>
          )}
          <button
            onClick={() => onDelete(m.id)}
            className="text-xs font-medium px-2 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800/60 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>
    );
  };

  // Sub-grup pe departament în interiorul panoului CLM. Numele departamentului e o etichetă
  // discretă (uppercase), nu un titlu mare — panoul în sine deja semnalează „CLM".
  const Section = ({ title, mandates }) => (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5">
        {title}
      </p>
      {mandates.length === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-500 italic">{t('clm.noClmYet')}</p>
      ) : (
        <div className="space-y-2">
          {mandates.map((m) =>
            renderMandate(
              m,
              handleEndNow,
              handleDelete,
              (mm) => setEditing({ mandate: mm, isAdmin: false }),
              'clm'
            )
          )}
        </div>
      )}
    </div>
  );

  const clmCount =
    data.departments.reduce((s, d) => s + d.mandates.length, 0) + data.companyWide.length;
  const adminCount = (data.adminMandates || []).length;

  return (
    <Card>
      {/* ── Panou 1: Conducători loc de muncă (accent INDIGO) ── */}
      <div className="rounded-xl border border-indigo-200/70 dark:border-indigo-900/50 overflow-hidden mb-6">
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-indigo-50/70 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-900/50">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
              <IconUsers />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {t('clm.title')}
                </h2>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                  {clmCount}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('clm.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => setModal({ departmentId: null })}
            className="flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            + {t('clm.addBtn')}
          </button>
        </div>
        <div className="p-4 space-y-5">
          {data.departments.map((d) => (
            <Section key={d.id} title={d.name} mandates={d.mandates} />
          ))}
          <Section title={t('clm.companyWide')} mandates={data.companyWide} />
        </div>
      </div>

      {/* ── Panou 2: Împuterniciri de administrator (accent AMBER) ── */}
      <div className="rounded-xl border border-amber-200/70 dark:border-amber-900/50 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50/70 dark:bg-amber-950/30 border-b border-amber-100 dark:border-amber-900/50">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300">
              <IconShield />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {t('clm.admin.title')}
                </h2>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                  {adminCount}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('clm.admin.subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setAdminModalOpen(true)}
            className="flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
          >
            + {t('clm.admin.addBtn')}
          </button>
        </div>
        <div className="p-4">
          {adminCount === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">
              {t('clm.admin.noneYet')}
            </p>
          ) : (
            <div className="space-y-2">
              {data.adminMandates.map((m) =>
                renderMandate(
                  m,
                  handleEndNowAdmin,
                  handleDeleteAdmin,
                  (mm) => setEditing({ mandate: mm, isAdmin: true }),
                  'admin'
                )
              )}
            </div>
          )}
        </div>
      </div>

      {modal && (
        <ClmModal
          companyId={companyId}
          departments={data.departments}
          eligibleUsers={data.eligibleUsers}
          initialDepartmentId={modal.departmentId}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            showToast(t('clm.added'), 'success');
            load();
          }}
        />
      )}

      {adminModalOpen && (
        <AdminModal
          companyId={companyId}
          eligibleUsers={data.eligibleAdminUsers || []}
          onClose={() => setAdminModalOpen(false)}
          onSaved={() => {
            setAdminModalOpen(false);
            showToast(t('clm.admin.added'), 'success');
            load();
          }}
        />
      )}

      {editing && (
        <EditMandateModal
          companyId={companyId}
          mandate={editing.mandate}
          isAdmin={editing.isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            showToast(t('clm.updated'), 'success');
            load();
          }}
        />
      )}

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'danger'}
        loading={confirmLoading}
      />

      {/* Încheie acum — motiv opțional */}
      <Modal
        open={!!endTarget}
        onClose={() => setEndTarget(null)}
        title={t('clm.endModal.title')}
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              size="md"
              onClick={() => setEndTarget(null)}
              disabled={endLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="primary" size="md" onClick={runEndNow} loading={endLoading}>
              {t('clm.endNow')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          {endTarget?.isAdmin ? t('clm.admin.confirmEndNow') : t('clm.confirmEndNow')}
        </p>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('clm.endModal.reasonLabel')}
        </label>
        <input
          type="text"
          value={endReason}
          onChange={(e) => setEndReason(e.target.value)}
          placeholder={t('clm.endModal.reasonPlaceholder')}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
        />
      </Modal>
    </Card>
  );
};

// Modal de adăugare mandat CLM.
const ClmModal = ({
  companyId,
  departments,
  eligibleUsers,
  initialDepartmentId,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  // coverage: 'company' | departmentId
  const [coverage, setCoverage] = useState(initialDepartmentId || 'company');
  const [startDate, setStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!userId) {
      setError(t('clm.modal.pickPerson'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await clmAPI.create(companyId, {
        userId,
        departmentId: coverage === 'company' ? null : coverage,
        startDate: startDate || undefined,
        reason: reason || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || t('clm.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('clm.modal.title')}
        </h3>

        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        )}

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('clm.modal.person')}
        </label>
        <div className="mb-4">
          <SearchableSelect
            value={userId}
            onChange={setUserId}
            placeholder={t('clm.admin.searchPerson')}
            options={eligibleUsers.map((u) => ({
              value: u.id,
              label: [`${u.lastName} ${u.firstName}`, u.email, u.departmentName]
                .filter(Boolean)
                .join(' · '),
            }))}
          />
        </div>

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('clm.modal.coverage')}
        </label>
        <select
          value={coverage}
          onChange={(e) => setCoverage(e.target.value)}
          className="w-full mb-4 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
        >
          <option value="company">{t('clm.modal.wholeCompany')}</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('clm.modal.startDate')}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
          />
        </div>

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('clm.modal.reason')}
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('clm.modal.reasonPlaceholder')}
          className="w-full mb-5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal de EDITARE a unui mandat (CLM sau admin) — perioadă + motiv. Persoana/acoperirea nu se schimbă.
const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const EditMandateModal = ({ companyId, mandate, isAdmin, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(toDateInput(mandate.startDate));
  const [reason, setReason] = useState(mandate.reason || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Editarea schimbă doar data de început și motivul. Încheierea se face din „Încheie acum".
      const payload = {
        startDate: startDate || undefined,
        reason: reason || '',
      };
      if (isAdmin) await clmAPI.updateAdmin(companyId, mandate.id, payload);
      else await clmAPI.update(companyId, mandate.id, payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || t('clm.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
          {t('clm.edit.title')}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          {mandate.user ? `${mandate.user.lastName} ${mandate.user.firstName}` : '—'}
        </p>

        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('clm.modal.startDate')}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
          />
        </div>

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('clm.modal.reason')}
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full mb-5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal de adăugare împuternicire de admin (persoană → puteri de COMPANY_ADMIN, permanent/temporar).
const AdminModal = ({ companyId, eligibleUsers, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!userId) {
      setError(t('clm.modal.pickPerson'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await clmAPI.createAdmin(companyId, {
        userId,
        startDate: startDate || undefined,
        reason: reason || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || t('clm.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
          {t('clm.admin.modalTitle')}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          {t('clm.admin.modalHint')}
        </p>

        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        )}

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('clm.modal.person')}
        </label>
        <div className="mb-4">
          <SearchableSelect
            value={userId}
            onChange={setUserId}
            placeholder={t('clm.admin.searchPerson')}
            options={eligibleUsers.map((u) => ({
              value: u.id,
              label: [`${u.lastName} ${u.firstName}`, u.email, u.departmentName]
                .filter(Boolean)
                .join(' · '),
            }))}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('clm.modal.startDate')}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
          />
        </div>

        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('clm.modal.reason')}
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('clm.admin.reasonPlaceholder')}
          className="w-full mb-5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClmManagement;
